import React from 'react';
import Layout from './Layout';
import { getCourseById, individualCourses } from '../data/courses';
import { formatPrice } from '../utils/helpers';
import './ComboTotalPage.css';

const INSTALLMENTS = 12;

function formatBRL(value) {
  return `R$ ${value.toFixed(2).replace('.', ',')}`;
}

function ComboTotalPage({ paymentUrlOverride }) {
  const combo = getCourseById('combo-total');
  const totalIndividualPrice = individualCourses.reduce((sum, c) => sum + c.price, 0);
  const savings = totalIndividualPrice - combo.price;
  const discountPercent = Math.round((savings / totalIndividualPrice) * 100);
  const paymentUrl = paymentUrlOverride || combo.paymentUrl;
  const installmentValue = combo.price / INSTALLMENTS;
  const pricePerCourse = combo.price / individualCourses.length;
  const installmentPerCourse = installmentValue / individualCourses.length;

  return (
    <Layout title="Combo Total — 18 Cursos de Xadrez" favicon="/todos.ico">
      {/* Hero */}
      <section className="combo-hero">
        <div className="combo-hero__inner">
          {combo.image && (
            <img src={combo.image} alt="Combo Total" className="combo-hero__image" />
          )}
          <div className="combo-hero__content">
            <span className="combo-hero__badge">18 cursos em até 12x</span>
            <h1 className="combo-hero__title">Desbloqueie sua Evolução no Xadrez</h1>
            <p className="combo-hero__subtitle">
              Domine Aberturas, Tática, Estratégia e Finais com o Grande Mestre André Diamant
              em 18 cursos completos.
            </p>
            <div className="combo-hero__price">
              <span className="combo-hero__price-label">Parcele em até</span>
              <div className="combo-hero__price-main">
                <span className="combo-hero__price-x">12x</span>
                <span className="combo-hero__price-value">de {formatBRL(installmentValue)}</span>
              </div>
              <span className="combo-hero__price-note">
                ou {formatPrice(combo.price)} à vista
              </span>
            </div>
            <a href={paymentUrl} target="_blank" rel="noopener noreferrer" className="combo-hero__cta">
              Quero o Combo em 12x
            </a>
            <p className="combo-hero__cta-note">
              Pague em 12x no cartão &middot; Acesso imediato após a compra
            </p>
          </div>
        </div>
      </section>

      {/* Included Courses */}
      <section className="combo-courses">
        <h2 className="combo-courses__title">18 Cursos Incluídos no Combo</h2>
        <p className="combo-courses__subtitle">
          Cada curso pode ser comprado individualmente — juntos, saem por 12x de{' '}
          {formatBRL(installmentValue)}
        </p>
        <div className="combo-courses__grid">
          {individualCourses.map(course => (
            <div key={course.id} className="combo-courses__item">
              <span className="combo-courses__item-category">{course.category}</span>
              <span className="combo-courses__item-name">{course.name}</span>
              <span className="combo-courses__item-price">
                Avulso: <span>{formatPrice(course.price)}</span>
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Savings */}
      <section className="combo-savings">
        <h2 className="combo-savings__title">Por que o Combo Total é imbatível?</h2>
        <div className="combo-savings__cards">
          <div className="combo-savings__card">
            <div className="combo-savings__card-label">Se comprados individualmente</div>
            <div className="combo-savings__card-value combo-savings__card-value--old">
              {formatPrice(totalIndividualPrice)}
            </div>
          </div>
          <div className="combo-savings__card combo-savings__card--highlight">
            <div className="combo-savings__card-label">No Combo Total</div>
            <div className="combo-savings__card-value">12x de {formatBRL(installmentValue)}</div>
            <div className="combo-savings__card-note">ou {formatPrice(combo.price)} à vista</div>
          </div>
          <div className="combo-savings__card combo-savings__card--savings">
            <div className="combo-savings__card-label">Você economiza</div>
            <div className="combo-savings__card-value">{formatPrice(savings)}</div>
          </div>
        </div>
        <p className="combo-savings__discount">
          Isso é {discountPercent}% de desconto!
        </p>
        <a href={paymentUrl} target="_blank" rel="noopener noreferrer" className="combo-savings__cta">
          Aproveitar em 12x
        </a>
      </section>

      {/* Installments highlight */}
      <section className="combo-installments">
        <span className="combo-installments__label">Cabe no seu bolso</span>
        <div className="combo-installments__main">
          <span className="combo-installments__x">12x</span>
          <span className="combo-installments__value">de {formatBRL(installmentValue)}</span>
        </div>
        <p className="combo-installments__note">
          ou {formatPrice(combo.price)} à vista — 18 cursos com acesso vitalício
        </p>
        <div className="combo-installments__cards">
          <div className="combo-installments__card">
            <div className="combo-installments__card-value">{formatBRL(installmentPerCourse)}</div>
            <div className="combo-installments__card-label">
              por curso, por mês, durante 12 meses
            </div>
          </div>
          <div className="combo-installments__card">
            <div className="combo-installments__card-value">{formatBRL(pricePerCourse)}</div>
            <div className="combo-installments__card-label">é o preço final de cada um dos 18 cursos</div>
          </div>
          <div className="combo-installments__card">
            <div className="combo-installments__card-value">12x</div>
            <div className="combo-installments__card-label">no cartão de crédito, com acesso liberado na hora</div>
          </div>
        </div>
        <a href={paymentUrl} target="_blank" rel="noopener noreferrer" className="combo-installments__cta">
          Quero parcelar em 12x
        </a>
      </section>

      {/* Features */}
      <section className="combo-features">
        <h2 className="combo-features__title">O que você recebe</h2>
        <div className="combo-features__grid">
          <div className="combo-features__item">
            <span className="combo-features__icon">&#127916;</span>
            <div>
              <div className="combo-features__text-title">Videoaulas em HD</div>
              <div className="combo-features__text-desc">Conteúdo gravado em alta qualidade com o GM André Diamant</div>
            </div>
          </div>
          <div className="combo-features__item">
            <span className="combo-features__icon">&#9854;</span>
            <div>
              <div className="combo-features__text-title">Acesso Vitalício</div>
              <div className="combo-features__text-desc">Estude no seu ritmo, sem prazo para expirar</div>
            </div>
          </div>
          <div className="combo-features__item">
            <span className="combo-features__icon">&#127942;</span>
            <div>
              <div className="combo-features__text-title">Certificado de Conclusão</div>
              <div className="combo-features__text-desc">Receba certificado ao finalizar cada curso</div>
            </div>
          </div>
          <div className="combo-features__item">
            <span className="combo-features__icon">&#128241;</span>
            <div>
              <div className="combo-features__text-title">Acesse de Qualquer Lugar</div>
              <div className="combo-features__text-desc">Computador, tablet ou celular — estude onde quiser</div>
            </div>
          </div>
          <div className="combo-features__item">
            <span className="combo-features__icon">&#9823;</span>
            <div>
              <div className="combo-features__text-title">18 Cursos Completos</div>
              <div className="combo-features__text-desc">Aberturas, Tática, Estratégia, Finais e muito mais</div>
            </div>
          </div>
          <div className="combo-features__item combo-features__item--highlight">
            <span className="combo-features__icon">&#128179;</span>
            <div>
              <div className="combo-features__text-title">Parcele em até 12x</div>
              <div className="combo-features__text-desc">
                12x de {formatBRL(installmentValue)} no cartão, ou {formatPrice(combo.price)} à vista
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="combo-final-cta">
        <h2 className="combo-final-cta__title">Comece sua evolução agora</h2>
        <p className="combo-final-cta__subtitle">
          18 cursos completos por menos do que o preço de um único curso
        </p>
        <div className="combo-final-cta__price">12x de {formatBRL(installmentValue)}</div>
        <p className="combo-final-cta__installments">
          ou {formatPrice(combo.price)} à vista
        </p>
        <a href={paymentUrl} target="_blank" rel="noopener noreferrer" className="combo-final-cta__btn">
          Comprar em 12x
        </a>
      </section>

      {/* Trust */}
      <section className="combo-trust">
        <div className="combo-trust__grid">
          <div className="combo-trust__item">
            <div className="combo-trust__icon">&#128274;</div>
            <div className="combo-trust__title">Pagamento Seguro</div>
            <div className="combo-trust__text">Seus dados protegidos com criptografia</div>
          </div>
          <div className="combo-trust__item">
            <div className="combo-trust__icon">&#9889;</div>
            <div className="combo-trust__title">Acesso Imediato</div>
            <div className="combo-trust__text">Comece a estudar logo após o pagamento</div>
          </div>
          <div className="combo-trust__item">
            <div className="combo-trust__icon">&#128179;</div>
            <div className="combo-trust__title">Até 12x no Cartão</div>
            <div className="combo-trust__text">12x de {formatBRL(installmentValue)} ou à vista</div>
          </div>
          <div className="combo-trust__item">
            <div className="combo-trust__icon">&#128176;</div>
            <div className="combo-trust__title">7 Dias de Garantia</div>
            <div className="combo-trust__text">Devolução integral se não ficar satisfeito</div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

export default ComboTotalPage;
